/**
 * React Query hooks for admin workspace management.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import {
  adminGetAllWorkspaces,
  adminGetWorkspaceDetail,
  adminUpdateWorkspace,
  adminBlockWorkspace,
  adminUnblockWorkspace,
  adminSoftDeleteWorkspace,
  adminRestoreWorkspace,
  adminAddWorkspaceCollaboratorDirect,
  adminRemoveWorkspaceCollaborator,
  adminGetCollaboratorDetail,
  adminUpdateCollaborator,
  adminGetWorkspaceProjects,
  adminGetWorkspaceTemplates,
  adminAssignProjectTemplates,
  adminGetProjectCollaborators,
  adminAddProjectCollaboratorDirect,
  adminUpdateProjectCollaborator,
  adminRemoveProjectCollaborator,
  adminGetWorkspaceCollaborators,
  adminUpdateWorkspaceCollaboratorRole,
  adminGetWorkspaceBoostUsage,
  adminGetWorkspaceBoostTransactions,
  adminGetAllWorkspacesBoostSummary,
  adminGetWorkspaceActivity,
  adminGetCostsByOperation,
  adminGetPricingConfig,
  adminGrantBoosts,
  adminGetWorkspaceContract,
  adminUpdateWorkspaceContract,
  getWorkspaceBoostHistory,
  adminGetBoostGateConfig,
  adminUpdateBoostGateConfig,
  type AdminWorkspacesResponse,
  type AdminWorkspaceDetail,
  type AdminWorkspace,
  type AdminCollaboratorDetailResponse,
  type WorkspaceCollaborator,
  type WorkspaceCollaboratorsResponse,
  type AdminBoostTransactionFilters,
  type AdminWorkflowFilter,
  type ProjectsResponse,
  type ProjectTemplatesResponse,
  type CollaboratorsResponse,
  type Collaborator,
  type WorkspaceBoostUsageResponse,
  type WorkspaceBoostTransactionsResponse,
  type AllWorkspacesBoostSummaryResponse,
  type WorkspaceActivityResponse,
  type CostsByOperationResponse,
  type PricingConfigResponse,
  type WorkspaceBoostHistoryResponse,
  type WorkspaceBoostHistoryParams,
  type BoostGateConfigResponse,
  adminGetCostsByWorkflow,
  type CostsByWorkflowResponse,
  type WorkspaceContract,
  type WorkspaceContractUpdate,
  type UpdateWorkspaceContractResponse,
} from "../services/naraDoor";
import { nextHistoryOffset } from "../lib/boostLedger";

interface UseAdminWorkspacesOptions {
  skip?: number;
  limit?: number;
  search?: string;
  includeBlocked?: boolean;
}

export function useAdminWorkspaces(options: UseAdminWorkspacesOptions = {}) {
  const { skip = 0, limit = 50, search, includeBlocked = true } = options;

  return useQuery<AdminWorkspacesResponse>({
    queryKey: ["adminWorkspaces", skip, limit, search, includeBlocked],
    queryFn: () => adminGetAllWorkspaces(skip, limit, search, includeBlocked),
  });
}

export function useAdminWorkspaceDetail(workspaceId: string | undefined) {
  return useQuery<AdminWorkspaceDetail>({
    queryKey: ["adminWorkspaceDetail", workspaceId],
    queryFn: () => {
      if (!workspaceId) {
        throw new Error("workspaceId is required");
      }
      return adminGetWorkspaceDetail(workspaceId);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminWorkspaceCollaborators(workspaceId: string | undefined) {
  return useQuery<WorkspaceCollaboratorsResponse>({
    queryKey: ["adminWorkspaceCollaborators", workspaceId],
    queryFn: () => {
      if (!workspaceId) {
        return Promise.resolve({ collaborators: [], total: 0 });
      }
      return adminGetWorkspaceCollaborators(workspaceId);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminWorkspaceProjects(workspaceId: string | undefined) {
  return useQuery<ProjectsResponse>({
    queryKey: ["adminWorkspaceProjects", workspaceId],
    queryFn: () => {
      if (!workspaceId) {
        return Promise.resolve({ projects: [], total: 0 });
      }
      return adminGetWorkspaceProjects(workspaceId);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminWorkspaceTemplates(workspaceId: string | undefined) {
  return useQuery<ProjectTemplatesResponse>({
    queryKey: ["adminWorkspaceTemplates", workspaceId],
    queryFn: () => {
      if (!workspaceId) {
        return Promise.resolve({ templates: [], total: 0 });
      }
      return adminGetWorkspaceTemplates(workspaceId);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminWorkspace,
    Error,
    { workspaceId: string; name?: string; handle?: string; settings?: Record<string, any>; is_dev?: boolean }
  >({
    mutationFn: ({ workspaceId, name, handle, settings, is_dev }) =>
      adminUpdateWorkspace(workspaceId, { name, handle, settings, is_dev }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaces"],
      });
    },
  });
}

export function useAdminBlockWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<
    AdminWorkspace,
    Error,
    { workspaceId: string; reason?: string }
  >({
    mutationFn: ({ workspaceId, reason }) =>
      adminBlockWorkspace(workspaceId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaces"],
      });
    },
  });
}

export function useAdminUnblockWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<AdminWorkspace, Error, { workspaceId: string }>({
    mutationFn: ({ workspaceId }) => adminUnblockWorkspace(workspaceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaces"],
      });
    },
  });
}

export function useAdminSoftDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<AdminWorkspace, Error, { workspaceId: string }>({
    mutationFn: ({ workspaceId }) => adminSoftDeleteWorkspace(workspaceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaces"],
      });
    },
  });
}

export function useAdminRestoreWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<AdminWorkspace, Error, { workspaceId: string }>({
    mutationFn: ({ workspaceId }) => adminRestoreWorkspace(workspaceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaces"],
      });
    },
  });
}

export function useAdminAddCollaboratorDirect() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkspaceCollaborator,
    Error,
    { workspaceId: string; userId: string; role: string }
  >({
    mutationFn: ({ workspaceId, ...data }) => adminAddWorkspaceCollaboratorDirect(workspaceId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceCollaborators", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
    },
  });
}

export function useAdminRemoveCollaborator() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { workspaceId: string; userId: string }>({
    mutationFn: ({ workspaceId, userId }) =>
      adminRemoveWorkspaceCollaborator(workspaceId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceCollaborators", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
    },
  });
}

export function useAdminCollaboratorDetail(
  workspaceId: string | undefined,
  userId: string | undefined
) {
  return useQuery<AdminCollaboratorDetailResponse>({
    queryKey: ["adminCollaboratorDetail", workspaceId, userId],
    queryFn: () => {
      if (!workspaceId || !userId) {
        throw new Error("workspaceId and userId are required");
      }
      return adminGetCollaboratorDetail(workspaceId, userId);
    },
    enabled: !!workspaceId && !!userId,
  });
}

export function useAdminUpdateCollaborator() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    {
      workspaceId: string;
      userId: string;
      email?: string;
      phone?: string;
      firstName?: string;
      lastName?: string;
    }
  >({
    mutationFn: ({ userId, email, phone, firstName, lastName }) =>
      adminUpdateCollaborator(userId, { email, phone, firstName, lastName }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminCollaboratorDetail", variables.workspaceId, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceCollaborators", variables.workspaceId],
      });
    },
  });
}

export function useAdminAssignTemplates() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { workspaceId: string; templateIds: string[] }>({
    mutationFn: ({ workspaceId, templateIds }) =>
      adminAssignProjectTemplates(workspaceId, templateIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceTemplates", variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceDetail", variables.workspaceId],
      });
    },
  });
}

export function useAdminUpdateCollaboratorRole() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkspaceCollaborator,
    Error,
    {
      workspaceId: string;
      userId: string;
      role: string;
    }
  >({
    mutationFn: ({ workspaceId, userId, role }) =>
      adminUpdateWorkspaceCollaboratorRole(workspaceId, userId, { role: role as any }),
    onSuccess: (_, variables) => {
      // Invalidate admin queries
      queryClient.invalidateQueries({
        queryKey: ["adminCollaboratorDetail", variables.workspaceId, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["adminWorkspaceCollaborators", variables.workspaceId],
      });
      // Also invalidate regular workspace queries for consistency
      queryClient.invalidateQueries({
        queryKey: ["workspaceCollaborators", variables.workspaceId],
      });
    },
  });
}

// =====================================================
// Admin Project Collaborator Hooks
// =====================================================

export function useAdminProjectCollaborators(projectId: string | undefined) {
  return useQuery<CollaboratorsResponse>({
    queryKey: ["adminProjectCollaborators", projectId],
    queryFn: () => {
      if (!projectId) {
        return Promise.resolve({ collaborators: [], total: 0 });
      }
      return adminGetProjectCollaborators(projectId);
    },
    enabled: !!projectId,
  });
}

export function useAdminAddProjectCollaborator() {
  const queryClient = useQueryClient();

  return useMutation<
    Collaborator,
    Error,
    { projectId: string; userId: string; emailTranscription?: boolean }
  >({
    mutationFn: ({ projectId, ...data }) => adminAddProjectCollaboratorDirect(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminProjectCollaborators", variables.projectId],
      });
    },
  });
}

export function useAdminUpdateProjectCollaborator() {
  const queryClient = useQueryClient();

  return useMutation<
    Collaborator,
    Error,
    {
      projectId: string;
      userId: string;
      emailTranscription?: boolean;
    }
  >({
    mutationFn: ({ projectId, userId, emailTranscription }) =>
      adminUpdateProjectCollaborator(projectId, userId, { emailTranscription }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminProjectCollaborators", variables.projectId],
      });
    },
  });
}

export function useAdminRemoveProjectCollaborator() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { projectId: string; userId: string }>({
    mutationFn: ({ projectId, userId }) =>
      adminRemoveProjectCollaborator(projectId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminProjectCollaborators", variables.projectId],
      });
    },
  });
}

// =====================================================
// Admin Boost Usage Hooks
// =====================================================

export function useAdminWorkspaceBoostUsage(workspaceId: string | undefined, workflow?: AdminWorkflowFilter) {
  return useQuery<WorkspaceBoostUsageResponse>({
    queryKey: ["adminWorkspaceBoostUsage", workspaceId, workflow],
    // Keep the page on screen while a workflow filter reloads the list.
    placeholderData: keepPreviousData,
    queryFn: () => {
      if (!workspaceId) {
        throw new Error("workspaceId is required");
      }
      return adminGetWorkspaceBoostUsage(workspaceId, workflow);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminWorkspaceBoostTransactions(
  workspaceId: string | undefined,
  limit?: number,
  offset?: number,
  startDate?: string,
  endDate?: string,
  filters?: AdminBoostTransactionFilters
) {
  return useQuery<WorkspaceBoostTransactionsResponse>({
    queryKey: ["adminWorkspaceBoostTransactions", workspaceId, limit, offset, startDate, endDate, filters],
    queryFn: () => {
      if (!workspaceId) {
        throw new Error("workspaceId is required");
      }
      return adminGetWorkspaceBoostTransactions(workspaceId, limit, offset, startDate, endDate, filters);
    },
    enabled: !!workspaceId,
  });
}

export function useAdminAllWorkspacesBoostSummary(days?: number) {
  return useQuery<AllWorkspacesBoostSummaryResponse>({
    queryKey: ["adminAllWorkspacesBoostSummary", days],
    queryFn: () => adminGetAllWorkspacesBoostSummary(days),
  });
}

export function useAdminWorkspaceActivity(days?: number) {
  return useQuery<WorkspaceActivityResponse>({
    queryKey: ["adminWorkspaceActivity", days],
    queryFn: () => adminGetWorkspaceActivity(days),
  });
}

export function useAdminCostsByOperation(days?: number) {
  return useQuery<CostsByOperationResponse>({
    queryKey: ["admin", "costs-by-operation", days],
    queryFn: () => adminGetCostsByOperation(days),
  });
}

export function useAdminPricingConfig() {
  return useQuery<PricingConfigResponse>({
    queryKey: ["admin", "pricing-config"],
    queryFn: () => adminGetPricingConfig(),
  });
}

export function useAdminCostsByWorkflow(workspaceId: string | undefined, days?: number) {
  return useQuery<CostsByWorkflowResponse>({
    queryKey: ["admin", "costs-by-workflow", workspaceId, days],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminGetCostsByWorkflow(workspaceId, days);
    },
    enabled: !!workspaceId,
  });
}

// =====================================================
// Workspace Boost History (User-facing)
// =====================================================

export function workspaceBoostHistoryOptions(
  workspaceId: string | undefined,
  filters: Omit<WorkspaceBoostHistoryParams, "offset">
) {
  // The filters are in the key: changing one starts again at offset 0.
  return {
    queryKey: ["workspaceBoostHistory", workspaceId, filters],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      try {
        return await getWorkspaceBoostHistory(workspaceId!, { ...filters, offset: pageParam });
      } catch (err) {
        console.error("getWorkspaceBoostHistory failed", { workspaceId, offset: pageParam, error: (err as Error).message });
        throw err;
      }
    },
    getNextPageParam: (_last: WorkspaceBoostHistoryResponse, pages: WorkspaceBoostHistoryResponse[]) =>
      nextHistoryOffset(pages),
    enabled: !!workspaceId,
  };
}

export function useWorkspaceBoostHistory(
  workspaceId: string | undefined,
  filters: Omit<WorkspaceBoostHistoryParams, "offset">
) {
  return useInfiniteQuery(workspaceBoostHistoryOptions(workspaceId, filters));
}

export function useAdminGrantBoosts(workspaceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    { newBalance: number },
    Error,
    { amount: number; reason: string }
  >({
    mutationFn: ({ amount, reason }) => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminGrantBoosts(workspaceId, amount, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceBoostUsage", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceBoostTransactions", workspaceId] });
    },
  });
}

export function useAdminWorkspaceContract(workspaceId: string | undefined) {
  return useQuery<WorkspaceContract>({
    queryKey: ["adminWorkspaceContract", workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminGetWorkspaceContract(workspaceId).catch((error: Error) => {
        console.error("adminGetWorkspaceContract failed", { workspaceId, error: error.message });
        throw error;
      });
    },
    enabled: !!workspaceId,
  });
}

export function useAdminUpdateWorkspaceContract(workspaceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<UpdateWorkspaceContractResponse, Error, WorkspaceContractUpdate>({
    mutationFn: (contract) => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminUpdateWorkspaceContract(workspaceId, contract);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceContract", workspaceId] });
      // A top-up may have run: balance, usage and ledger all move.
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceBoostUsage", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceBoostTransactions", workspaceId] });
    },
    onError: (error) => {
      console.error("adminUpdateWorkspaceContract failed", { workspaceId, error: error.message });
    },
  });
}

// =====================================================
// Admin Boost Gate Config Hooks
// =====================================================

export function useAdminBoostGateConfig() {
  return useQuery<BoostGateConfigResponse>({
    queryKey: ["admin", "boost-gate-config"],
    queryFn: () => adminGetBoostGateConfig(),
  });
}

export function useAdminUpdateBoostGateConfig() {
  const queryClient = useQueryClient();

  return useMutation<
    BoostGateConfigResponse,
    Error,
    { workflows: { workflowType: string; minimumBoosts: number; coverCharge: number; minimumCost: number }[] }
  >({
    mutationFn: ({ workflows }) => adminUpdateBoostGateConfig(workflows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "boost-gate-config"] });
    },
  });
}
